import { Stack, Group, Text, Title, Paper, Button, ThemeIcon, SegmentedControl, Badge } from '@mantine/core';
import {
  IconArrowLeft,
  IconArrowRight,
  IconWand,
  IconChefHat,
  IconArrowsShuffle,
  IconListCheck,
  IconBookmark,
} from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { PlanningMode } from '../../../types/mealPlan';
import { useRecipes } from '../../../hooks/useRecipes';
import { formatCount } from '../../../utils/formatCount';

export type ModeStepValue = {
  mode: PlanningMode | null;
  noveltyDays: number; // shuffle mode only: 0-3 new ideas mixed into the rotation
};

type ModeStepProps = {
  value: ModeStepValue;
  onChange: (value: ModeStepValue) => void;
  onNext: () => void;
  onBack: () => void;
  dateRange: [string | null, string | null];
  uniqueMeals?: number;
};

const MAX_NOVELTY_DAYS = 3;

type ModeOption = {
  mode: PlanningMode;
  icon: React.ReactNode;
  title: string;
  description: string;
};

const MODE_OPTIONS: ModeOption[] = [
  {
    mode: 'surprise_me',
    icon: <IconWand size={22} />,
    title: 'Surprise me',
    description: "We'll pick every meal for you, tailored to your family's tastes.",
  },
  {
    mode: 'shuffle_cookbook',
    icon: <IconArrowsShuffle size={22} />,
    title: 'Shuffle my recipes',
    description: 'A rotation from your cookbook. Meals you already know you love.',
  },
  {
    mode: 'pick_a_few',
    icon: <IconListCheck size={22} />,
    title: 'Let me pick a few',
    description: "Choose meals from a short list of ideas. We'll fill in the rest.",
  },
];

export const ModeStep = ({ value, onChange, onNext, onBack, dateRange, uniqueMeals }: ModeStepProps) => {
  // Count only — the mode options never show the dashboard's sample recipes
  const { cookbookCount, loading: recipesLoading } = useRecipes({ withSamples: false });
  const isNarrow = useMediaQuery('(max-width: 48em)');

  // Meals the plan needs; shuffle is only viable when the cookbook can cover
  // them (less any new ideas the novelty dial mixes in)
  let totalDays = 7;
  if (dateRange[0] && dateRange[1]) {
    const diffTime = Math.abs(new Date(dateRange[1]).getTime() - new Date(dateRange[0]).getTime());
    totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }
  const mealsNeeded = uniqueMeals ?? totalDays;
  const shuffleShortfall = Math.max(0, mealsNeeded - MAX_NOVELTY_DAYS - cookbookCount);
  const shuffleDisabled = !recipesLoading && shuffleShortfall > 0;

  const selectMode = (mode: PlanningMode) => {
    if (mode === 'shuffle_cookbook' && shuffleDisabled) return;
    // Clamp the dial so "just my recipes" isn't selectable when the cookbook
    // can't cover every meal on its own
    const minNovelty =
      mode === 'shuffle_cookbook' ? Math.max(0, mealsNeeded - cookbookCount) : 0;
    onChange({ mode, noveltyDays: Math.max(value.noveltyDays, minNovelty) });
  };

  const noveltyOptions = [
    { label: 'Just my recipes', value: '0', needed: mealsNeeded },
    { label: '+1 new idea', value: '1', needed: mealsNeeded - 1 },
    { label: '+2 new ideas', value: '2', needed: mealsNeeded - 2 },
    { label: '+3 new ideas', value: '3', needed: mealsNeeded - 3 },
  ];

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconChefHat size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          <Title order={2} size="h1" fw={700} c="dark">
            How should we build it?
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={400}>
            You can shape any plan after it's created. This just sets the starting point.
          </Text>
        </Stack>
      </Stack>

      {/* Mode cards */}
      <Stack gap="md">
        {MODE_OPTIONS.map((option) => {
          const selected = value.mode === option.mode;
          const disabled = option.mode === 'shuffle_cookbook' && shuffleDisabled;
          return (
            <Paper
              key={option.mode}
              radius="lg"
              p={{ base: 'lg', sm: 'xl' }}
              onClick={() => selectMode(option.mode)}
              style={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                // Constant border width; the selected ring is box-shadow so
                // toggling never shifts layout
                border: `1px solid ${
                  selected ? 'var(--mantine-color-orange-5)' : 'var(--mantine-color-gray-2)'
                }`,
                boxShadow: selected
                  ? '0 0 0 1px var(--mantine-color-orange-5), var(--mantine-shadow-md)'
                  : undefined,
                opacity: disabled ? 0.6 : 1,
                transition: 'border-color 120ms ease, box-shadow 120ms ease',
              }}
            >
              <Stack gap="sm">
                <Group gap="sm" align="flex-start" wrap="nowrap">
                  <ThemeIcon
                    size={40}
                    radius="md"
                    color="orange"
                    variant={selected ? 'filled' : 'light'}
                  >
                    {option.icon}
                  </ThemeIcon>
                  <Stack gap={2} style={{ flex: 1 }}>
                    <Text fw={600} size="lg">
                      {option.title}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {option.description}
                    </Text>
                  </Stack>
                </Group>

                {/* Shuffle: cookbook-size nudge */}
                {option.mode === 'shuffle_cookbook' && disabled && (
                  <Group gap="xs" pl={52} align="flex-start" wrap="nowrap">
                    <IconBookmark
                      size={14}
                      color="var(--mantine-color-orange-6)"
                      style={{ flexShrink: 0, marginTop: 2 }}
                    />
                    <Text size="xs" c="dimmed">
                      You have {formatCount(cookbookCount)} saved recipe{cookbookCount !== 1 ? 's' : ''}. Save{' '}
                      {shuffleShortfall} more to unlock a full week from your cookbook.
                    </Text>
                  </Group>
                )}

                {/* Shuffle: novelty dial */}
                {option.mode === 'shuffle_cookbook' && selected && !disabled && (
                  <Stack gap={6} pl={52}>
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        Sprinkle in something new?
                      </Text>
                      <Badge size="sm" variant="light" color="orange">
                        Optional
                      </Badge>
                    </Group>
                    <SegmentedControl
                      value={String(value.noveltyDays)}
                      onChange={(v) => onChange({ ...value, noveltyDays: Number(v) })}
                      data={noveltyOptions.map((opt) => ({
                        label: opt.label,
                        value: opt.value,
                        disabled: cookbookCount < opt.needed,
                      }))}
                      color="orange"
                      size="sm"
                      orientation={isNarrow ? 'vertical' : 'horizontal'}
                      fullWidth
                    />
                  </Stack>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {/* Navigation */}
      <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
        <Button
          variant="subtle"
          size="md"
          leftSection={<IconArrowLeft size={18} />}
          onClick={onBack}
          color="orange"
        >
          Back
        </Button>

        <Button
          size="md"
          rightSection={<IconArrowRight size={18} />}
          onClick={onNext}
          disabled={value.mode === null}
          color="orange"
        >
          Continue
        </Button>
      </Group>
    </Stack>
  );
};
